<template>
  <b-modal
    :value="visible"
    title="Dodaj nową lokalizację"
    @ok="handleCreateLocale"
    @hidden="handleHidden"
    @input="$emit('input', $event)">
    <validation-observer ref="createLocaleForm">
      <b-form @submit.prevent="handleCreateLocale">
        <b-form-group label="Kod językowy*" label-for="locale-code">
          <validation-provider #default="{ errors }" name="Kod językowy" rules="required|min:2|max:10">
            <b-form-input
              id="locale-code"
              :value="newLocale.code"
              placeholder="np. en-US, de-DE"
              :state="errors.length === 0 ? null : false"
              @input="updateNewLocale('code', $event)" />
            <small v-if="errors.length" class="text-danger">
              {{ errors[0] }}
            </small>
          </validation-provider>
        </b-form-group>

        <b-form-group label="Nazwa języka*" label-for="locale-name">
          <validation-provider #default="{ errors }" name="Nazwa języka" rules="required|min:2|max:50">
            <b-form-input
              id="locale-name"
              :value="newLocale.name"
              placeholder="np. English (US), Deutsch (Deutschland)"
              :state="errors.length === 0 ? null : false"
              @input="updateNewLocale('name', $event)" />
            <small v-if="errors.length" class="text-danger">
              {{ errors[0] }}
            </small>
          </validation-provider>
        </b-form-group>

        <b-form-group label="Źródłowa lokalizacja" label-for="source-locale">
          <b-form-select
            id="source-locale"
            :value="newLocale.sourceLocale"
            :options="localeOptions"
            @change="updateNewLocale('sourceLocale', $event)">
            <template #first>
              <b-form-select-option :value="null">Pusta lokalizacja</b-form-select-option>
            </template>
          </b-form-select>
          <small class="text-muted">Skopiuj tłumaczenia z istniejącej lokalizacji</small>
        </b-form-group>

        <b-alert v-if="newLocale.sourceLocale" variant="info" show class="mb-0">
          <feather-icon icon="InfoIcon" size="16" class="mr-1" />
          Tłumaczenia zostaną skopiowane z lokalizacji: <strong>{{ getSourceLocaleName }}</strong>
        </b-alert>
      </b-form>
    </validation-observer>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel"> Anuluj </b-button>
      <b-button variant="primary" :disabled="!isFormValid" @click="ok">
        <feather-icon icon="PlusIcon" size="16" class="mr-1" />
        Utwórz lokalizację
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

export default {
  name: 'CreateLocaleModal',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    newLocale: {
      type: Object,
      default: () => ({
        code: '',
        name: '',
        sourceLocale: null,
      }),
    },
    localeOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['input', 'create-locale', 'reset', 'update:new-locale'],
  setup(props, { emit }) {
    const createLocaleForm = ref(null);

    const isFormValid = computed(() => {
      return props.newLocale.code.trim() !== '' && props.newLocale.name.trim() !== '';
    });

    const getSourceLocaleName = computed(() => {
      if (!props.newLocale.sourceLocale) return '';
      const sourceLocale = props.localeOptions.find((option) => option.value === props.newLocale.sourceLocale);
      return sourceLocale ? sourceLocale.text : props.newLocale.sourceLocale;
    });

    const handleCreateLocale = async () => {
      if (!createLocaleForm.value) return;

      const isValid = await createLocaleForm.value.validate();
      if (!isValid) return;

      emit('create-locale');
    };

    const handleHidden = () => {
      if (createLocaleForm.value) {
        createLocaleForm.value.reset();
      }
      emit('reset');
    };

    const updateNewLocale = (key, value) => {
      const updatedLocale = { ...props.newLocale, [key]: value };
      emit('update:new-locale', updatedLocale);
    };

    return {
      createLocaleForm,
      isFormValid,
      getSourceLocaleName,
      handleCreateLocale,
      handleHidden,
      updateNewLocale,
    };
  },
};
</script>
